import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, KeyRound, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  admin: "Administrator",
  compliance: "Compliance",
  legal: "Rechtsabteilung",
  viewer: "Nur-Lesen",
};

const ROLE_BADGE = {
  admin: "bg-[#0A0A0A] text-white",
  compliance: "bg-[#E5E1DB] text-[#0A0A0A]",
  legal: "bg-[#A6A4A9] text-white",
  viewer: "border border-[#E0DCD6] bg-[#FAF7F2] text-[#6B6A6E]",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "compliance", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch {
      toast.error("Benutzer konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email.trim() || !form.password) {
      toast.error("E-Mail und Passwort sind erforderlich.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        password: form.password,
      });
      toast.success("Benutzer angelegt.");
      setCreateOpen(false);
      setForm({ name: "", email: "", role: "compliance", password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success("Rolle aktualisiert.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen.");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Benutzer deaktiviert." : "Benutzer aktiviert.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen.");
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Mindestens 6 Zeichen.");
      return;
    }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { password: newPassword });
      toast.success("Passwort zurückgesetzt.");
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zurücksetzen fehlgeschlagen.");
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Benutzer gelöscht.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  return (
    <div className="space-y-8" data-testid="users-page">
      <PageHeader title="Benutzer" subtitle="Teammitglieder anlegen und Rollen verwalten">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-create-user" className="rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]">
              <UserPlus className="mr-2 h-4 w-4" /> Benutzer anlegen
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-lg border-[#E8E4DF]" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Neuen Benutzer anlegen</DialogTitle>
              <DialogDescription>Vergeben Sie Name, E-Mail, Rolle und ein initiales Passwort.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="u-name">Name</Label>
                <Input id="u-name" data-testid="create-user-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Vor- und Nachname"
                  className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="u-email">E-Mail *</Label>
                <Input id="u-email" data-testid="create-user-email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="name@firma.de"
                  className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]" />
              </div>
              <div className="space-y-1.5">
                <Label>Rolle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="create-user-role" className="rounded-md border-[#E0DCD6] bg-[#FAF7F2]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="u-pass">Passwort * (min. 6 Zeichen)</Label>
                <Input id="u-pass" data-testid="create-user-password" type="text" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Initiales Passwort"
                  className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-md">Abbrechen</Button>
              <Button data-testid="submit-create-user" onClick={create} disabled={submitting}
                className="rounded-md bg-[#0A0A0A] text-white hover:bg-[#262626]">
                {submitting ? "Speichert …" : "Anlegen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-0 shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        {loading ? (
          <p className="p-8 text-sm text-[#8B8A8E]">Lädt …</p>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <UsersIcon className="h-8 w-8 text-[#A6A4A9]" />
            <p className="text-sm text-[#8B8A8E]">Noch keine Benutzer.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#EDE9E3]">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`}
                className="flex flex-col gap-3 p-4 transition-colors hover:bg-[#FAF7F2] sm:flex-row sm:items-center sm:justify-between md:px-6">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-[#0A0A0A]">{u.name || u.email}</p>
                    {u.id === me?.id && (
                      <span className="rounded-sm bg-[#F4F1EC] px-1.5 py-0.5 text-[10px] font-semibold text-[#6B6A6E]">Sie</span>
                    )}
                    {!u.active && (
                      <span className="rounded-sm bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600">Inaktiv</span>
                    )}
                  </div>
                  <p className="truncate text-sm text-[#6B6A6E]">{u.email}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger data-testid={`role-select-${u.id}`}
                      className={`h-8 w-[150px] rounded-md border-0 text-xs font-semibold ${ROLE_BADGE[u.role] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button data-testid={`toggle-active-${u.id}`} variant="outline" size="sm"
                    onClick={() => toggleActive(u)} disabled={u.id === me?.id}
                    className="h-8 rounded-md border-[#E0DCD6] text-xs">
                    {u.active ? "Deaktivieren" : "Aktivieren"}
                  </Button>

                  <Button data-testid={`reset-pw-${u.id}`} variant="ghost" size="icon"
                    onClick={() => { setResetTarget(u); setNewPassword(""); }}
                    className="h-8 w-8 rounded-md text-[#A6A4A9] hover:bg-[#F4F1EC] hover:text-[#0A0A0A]"
                    title="Passwort zurücksetzen">
                    <KeyRound className="h-4 w-4" />
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" disabled={u.id === me?.id}
                        className="h-8 w-8 rounded-md text-[#A6A4A9] hover:bg-rose-50 hover:text-rose-600"
                        title="Löschen">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {u.name || u.email} wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-md">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-${u.id}`} onClick={() => remove(u)}
                          className="rounded-md bg-rose-600 text-white hover:bg-rose-700">Löschen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex items-start gap-2 rounded-md border border-[#E8E4DF] bg-[#F4F1EC] p-4 text-sm text-[#6B6A6E]">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0A0A0A]" />
        <span>
          <b className="text-[#0A0A0A]">Rollen:</b> Administrator (volle Rechte inkl. Benutzerverwaltung),
          Compliance (Scans &amp; Fälle bearbeiten), Rechtsabteilung (eskalierte Fälle), Nur-Lesen (Einsicht).
        </span>
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="rounded-lg border-[#E8E4DF]" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Passwort zurücksetzen</DialogTitle>
            <DialogDescription>Vergeben Sie ein neues Passwort für dieses Konto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="new-pw">Neues Passwort für {resetTarget?.email}</Label>
            <Input id="new-pw" data-testid="reset-pw-input" type="text" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
              className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} className="rounded-md">Abbrechen</Button>
            <Button data-testid="submit-reset-pw" onClick={resetPassword}
              className="rounded-md bg-[#0A0A0A] text-white hover:bg-[#262626]">Zurücksetzen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
